import { Head, router } from "@inertiajs/react"
import { useState, useCallback } from "react"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface AdminVideo {
  public_id: string
  name: string
  status: string
  aspect_ratio: string
  hero_image_url: string | null
  listing_address: string
  owner_name: string
  account_name: string
  total_cost_cents: number
  created_at: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  search: string | null
  status: string | null
  sort: string
  direction: string
  videos: AdminVideo[]
  pagination: PaginationMeta
  counts: {
    all: number
    draft: number
    scripted: number
    rendered: number
    published: number
    archived: number
  }
}

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "scripted", label: "Scripted" },
  { value: "rendered", label: "Rendered" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
] as const

const STATUS_COLOURS: Record<string, string> = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  scripted: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  rendered: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  published: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  archived: "bg-cream/10 text-cream/60 border-cream/20",
}

const COLUMNS = [
  { key: "image", label: "", sortable: false },
  { key: "name", label: "Name", sortable: true },
  { key: "status", label: "Status", sortable: false },
  { key: "listing", label: "Listing", sortable: false },
  { key: "owner", label: "Owner", sortable: false },
  { key: "account", label: "Account", sortable: false },
  { key: "total_cost_cents", label: "Cost", sortable: true },
  { key: "created_at", label: "Created", sortable: true },
] as const

export default function AdminVideosIndex({
  search,
  status,
  sort,
  direction,
  videos,
  pagination,
  counts,
}: Props) {
  const [searchValue, setSearchValue] = useState(search || "")

  const buildParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = {}
      if (search) p.search = search
      if (status) p.status = status
      if (sort !== "created_at") p.sort = sort
      if (direction !== "desc") p.direction = direction
      for (const [k, v] of Object.entries(overrides)) {
        if (v === undefined || v === "") {
          delete p[k]
        } else {
          p[k] = v
        }
      }
      return p
    },
    [search, status, sort, direction],
  )

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/videos",
      buildParams({ search: searchValue || undefined, page: undefined }),
      { preserveState: true },
    )
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    router.get(
      "/admin/videos",
      buildParams({ sort: column, direction: newDirection, page: undefined }),
      { preserveState: true },
    )
  }

  function handleFilter(value: string) {
    const val = value === "all" ? undefined : value
    router.get("/admin/videos", buildParams({ status: val, page: undefined }), {
      preserveState: true,
    })
  }

  return (
    <AdminLayout>
      <Head title="Videos | Admin" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-semibold text-cream">Videos</h1>
          <p className="mt-1 text-sm text-cream/50">
            {pagination.count} {pagination.count === 1 ? "video" : "videos"} across all accounts
          </p>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Select value={status || "all"} onValueChange={handleFilter}>
            <SelectTrigger className="w-[160px] border-white/10 bg-white/5 text-cream">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark border-white/10 bg-charcoal">
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                  {opt.value !== "all" && (
                    <span className="ml-1 text-[10px] opacity-50">
                      {counts[opt.value as keyof typeof counts]}
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-cream/30" />
              <Input
                placeholder="Search name or address..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-8 w-56 border-white/10 bg-white/5 pl-8 text-sm text-cream placeholder:text-cream/30"
              />
            </div>
            {search && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-cream/50 hover:text-cream"
                onClick={() => {
                  setSearchValue("")
                  router.get(
                    "/admin/videos",
                    buildParams({ search: undefined, page: undefined }),
                    { preserveState: true },
                  )
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </div>

        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40"
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 transition-colors hover:text-cream/70"
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                        <SortIcon
                          active={sort === col.key}
                          direction={sort === col.key ? direction : undefined}
                        />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {videos.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-cream/30">
                    {search ? `No videos matching "${search}"` : "No videos found"}
                  </td>
                </tr>
              )}
              {videos.map((video) => (
                <tr
                  key={video.public_id}
                  className="cursor-pointer border-b border-white/[0.04] text-cream/70 transition-colors hover:bg-white/[0.04]"
                  onClick={() => router.visit(`/admin/videos/${video.public_id}`)}
                >
                  <td className="px-4 py-2.5">
                    {video.hero_image_url ? (
                      <img
                        src={video.hero_image_url}
                        alt=""
                        className="size-10 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="flex size-10 shrink-0 items-center justify-center rounded bg-white/5 text-cream/20">
                        <VideoPlaceholderIcon className="size-5" />
                      </div>
                    )}
                  </td>
                  <td className="max-w-[160px] truncate px-4 py-2.5 font-medium text-cream">
                    {video.name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <Badge className={STATUS_COLOURS[video.status] || STATUS_COLOURS.draft}>
                      {video.status}
                    </Badge>
                  </td>
                  <td className="max-w-[140px] truncate px-4 py-2.5 text-cream/50">
                    {video.listing_address}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-cream/50">
                    {video.owner_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-cream/50">
                    {video.account_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs tabular-nums text-cream/50">
                    {formatCurrency(video.total_cost_cents)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-cream/50">
                    {formatDate(video.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination baseUrl="/admin/videos" params={buildParams()} pagination={pagination} />
      </div>
    </AdminLayout>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg
        className="size-3 opacity-30"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
        />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
      />
    </svg>
  )
}

function VideoPlaceholderIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
      />
    </svg>
  )
}

function formatCurrency(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1) return `$${dollars.toFixed(2)}`
  return `${cents}c`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}
